import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { fromPosition, POSITION_STEPS, toPosition } from '../utils/sliderScale'
import type { ScaleBound } from '../utils/sliderScale'

interface SliderProps {
  label: string
  value: number
  min: number
  max: number
  /** Ignored when `scale` is 'log' — travel is quantised by POSITION_STEPS instead. */
  step?: number
  /** Log gives money sliders usable resolution across four orders of magnitude. */
  scale?: 'linear' | 'log'
  onChange: (value: number) => void
  /**
   * Fires true on grab and false on release. Drives the simulation's fidelity
   * tier — cheap runs during the gesture, the full 10,000 paths once released.
   */
  onInteractingChange?: (interacting: boolean) => void
  format?: (value: number) => string
  /**
   * Supplying this makes the readout tappable and editable. Return null for
   * unparseable input and the previous value is kept.
   */
  parse?: (raw: string) => number | null
  /** Applied to the dragged value before it is committed, e.g. snapMoney. */
  snap?: (value: number) => number
  /**
   * A reference point drawn on the track — reality, against the user's choice.
   * Used for the current CPI print on the inflation slider, so "what I assume"
   * is shown next to "what it actually is" rather than in a footnote.
   */
  marker?: { value: number; label: string }
  hint?: string
}

/**
 * A reference tick on the track.
 *
 * Inset by half the thumb width at each end, because the thumb's centre can only
 * travel between 10px and (100% - 10px) — without the inset the tick drifts from
 * the value it marks, worst at the extremes, which is exactly where someone would
 * be checking it.
 */
function MarkerTick({ fraction, label }: { fraction: number; label: string }) {
  const left = `calc(${fraction} * (100% - 20px) + 10px)`
  return (
    <>
      <span
        aria-hidden="true"
        className="pointer-events-none absolute z-10"
        style={{
          left,
          top: 'calc(50% - 8px)',
          height: 16,
          width: 2,
          marginLeft: -1,
          borderRadius: 1,
          background: 'var(--c-fire)',
        }}
      />
      <span
        aria-hidden="true"
        className="numeral pointer-events-none absolute whitespace-nowrap text-[length:var(--text-micro)] text-[color:var(--c-fire)]"
        style={{ left, top: 'calc(50% + 10px)', transform: 'translateX(-50%)' }}
      >
        {label}
      </span>
    </>
  )
}

export function Slider({
  label,
  value,
  min,
  max,
  step = 1,
  scale = 'linear',
  onChange,
  onInteractingChange,
  format,
  parse,
  snap,
  marker,
  hint,
}: SliderProps) {
  const id = useId()
  const bound: ScaleBound = { min, max, scale }

  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const inputRef = useRef<HTMLInputElement | null>(null)
  /** True only between pointerdown and release, so the window net stays scoped. */
  const draggingRef = useRef(false)

  const end = useCallback(() => {
    draggingRef.current = false
    onInteractingChange?.(false)
  }, [onInteractingChange])

  const begin = useCallback(() => {
    draggingRef.current = true
    onInteractingChange?.(true)
  }, [onInteractingChange])

  /*
   * A release the element never sees still has to end the drag.
   *
   * `onPointerUp` on the input is the happy path and it holds on desktop, where
   * the browser implicitly captures the pointer for a range input. It is not
   * something to depend on across engines: a gesture the browser reclaims, a
   * finger lifted after the touch has been retargeted, or a pointer that leaves
   * the surface can all end a drag without the input hearing about it. If that
   * happens, `interacting` stays true forever — the simulation is pinned to the
   * cheap preview tier and never settles to the full run, so every number on
   * screen quietly stops being the real one.
   *
   * `draggingRef` gates it, and that gate is the point. Listening
   * unconditionally meant every Slider on the page held a pair of window
   * listeners for its whole life, so a second finger tapping anywhere — or a
   * cancelled scroll — ended the preview tier in the middle of somebody else's
   * drag, dropping the worker back to 10,000 paths for the rest of the gesture.
   */
  useEffect(() => {
    if (onInteractingChange === undefined) return
    const release = () => {
      if (!draggingRef.current) return
      draggingRef.current = false
      onInteractingChange(false)
    }
    window.addEventListener('pointerup', release)
    window.addEventListener('pointercancel', release)
    return () => {
      window.removeEventListener('pointerup', release)
      window.removeEventListener('pointercancel', release)
    }
  }, [onInteractingChange])

  // A log slider runs on the position domain; a linear one keeps its natural
  // units so keyboard stepping still moves in whole years.
  const isLog = scale === 'log'
  const sliderValue = isLog ? toPosition(value, bound) : value
  const sliderMin = isLog ? 0 : min
  const sliderMax = isLog ? POSITION_STEPS : max
  const sliderStep = isLog ? 1 : step

  const fill =
    sliderMax === sliderMin
      ? 0
      : ((sliderValue - sliderMin) / (sliderMax - sliderMin)) * 100

  const handleSlide = (raw: number) => {
    const next = isLog ? fromPosition(raw, bound) : raw
    onChange(snap ? snap(next) : next)
  }

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus()
      inputRef.current?.select()
    }
  }, [editing])

  const openEditor = () => {
    if (parse === undefined) return
    // Seed with the plain number, not the formatted string: "1250000" is far
    // easier to correct a digit in than "₹12.5 L".
    setDraft(String(Math.round(value)))
    setEditing(true)
  }

  const commit = () => {
    const parsed = parse?.(draft) ?? null
    // Unparseable input keeps the previous value rather than blanking the chart
    // behind a half-typed entry.
    // `snap` too, exactly as handleSlide does. Without it a typed value lands
    // off the grid every drag snaps to, so the thumb sits at a position the user
    // can never return to by dragging and the shared URL encodes an odd number.
    // No re-clamp: the parser was handed these same bounds and has applied them.
    if (parsed !== null) onChange(snap ? snap(parsed) : parsed)
    setEditing(false)
    end()
  }

  return (
    <div>
      <div className="flex items-baseline justify-between gap-3">
        <label
          htmlFor={id}
          className="text-[length:var(--text-label)] text-[color:var(--c-text-secondary)]"
        >
          {label}
        </label>

        {editing ? (
          <input
            ref={inputRef}
            type="text"
            // 'decimal' rather than 'numeric': it brings up the phone's number
            // pad, which is what almost everyone wants when correcting an amount.
            // It does hide the letters that shorthand like "50k" needs — parseINR
            // still accepts those from a desktop keyboard or a paste.
            inputMode="decimal"
            value={draft}
            aria-label={`${label}, edit value`}
            className="numeral w-32 rounded-[var(--radius-sm)] border border-[color:var(--c-accent)] bg-[color:var(--c-bg-elevated)] px-2 py-0.5 text-right text-[length:var(--text-body)] text-[color:var(--c-text-primary)]"
            onChange={(event) => setDraft(event.target.value)}
            onBlur={commit}
            onKeyDown={(event) => {
              if (event.key === 'Enter') commit()
              if (event.key === 'Escape') {
                setEditing(false)
                end()
              }
            }}
          />
        ) : (
          <button
            type="button"
            onClick={openEditor}
            // Without a parser there is nothing to open, so the readout stays
            // inert rather than advertising an interaction that does nothing.
            disabled={parse === undefined}
            aria-label={parse ? `${label}, tap to type a value` : undefined}
            className="numeral rounded-[var(--radius-sm)] px-1 text-[length:var(--text-body)] text-[color:var(--c-text-primary)] transition-colors duration-[var(--dur-fast)] enabled:hover:bg-[color:var(--c-bg-hover)] disabled:cursor-default"
          >
            {format ? format(value) : value}
          </button>
        )}
      </div>

      <div className="relative">
        {marker !== undefined && (
          <MarkerTick
            fraction={
              isLog
                ? toPosition(marker.value, bound) / POSITION_STEPS
                : max === min
                  ? 0
                  : (Math.min(max, Math.max(min, marker.value)) - min) / (max - min)
            }
            label={marker.label}
          />
        )}

        <input
          id={id}
          type="range"
          className="slider"
          min={sliderMin}
          max={sliderMax}
          step={sliderStep}
          value={sliderValue}
          // The visible readout is the accessible value; without this a screen
          // reader announces raw slider positions like "743" for a log control.
          aria-valuetext={format ? format(value) : String(value)}
          aria-label={label}
          style={{ ['--fill' as string]: `${fill}%` }}
          onChange={(event) => handleSlide(Number(event.target.value))}
          onPointerDown={begin}
          onPointerUp={end}
          onPointerCancel={end}
          // Keyboard adjustment gets the same treatment: arrow keys run the preview
          // tier, blur settles to the full run.
          onKeyDown={begin}
          onKeyUp={end}
          onBlur={end}
        />
      </div>

      {hint !== undefined && (
        <p className="mt-0.5 text-[length:var(--text-micro)] text-[color:var(--c-text-muted)]">
          {hint}
        </p>
      )}
    </div>
  )
}
